import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useTenantState from '@/states/tenant.state';
import useAuthState from '@/states/auth.state';
import { useGetCompanyData } from './company-data/useCompanyData';
import {
  useGetChecklistInstanceByCompany,
  useGetActiveChecklistTemplate,
  useCreateChecklistInstance,
  useUpdateChecklistResponse,
  useCompleteChecklistResponse,
  useConfirmChecklistInstance,
  useStartImplementation,
} from './checklist/useChecklist';
import { ChecklistView } from './checklist/ChecklistView';
import { ChecklistSkeleton } from './checklist/ChecklistSkeleton';
import { ChecklistLog } from './checklist/components/ChecklistLog';
import { ChecklistNotes } from './checklist/components/ChecklistNotes';
import { ChecklistDetail } from './checklist/components/ChecklistDetail';
import { ImplementationStartCard } from './checklist/components/ImplementationStartCard';
import { AlertCircle, Loader2, Check } from 'lucide-react';
import { SubmitButton, DefaultButton } from '@/components/ui/Button';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import useCompanyState from '@/states/company.state';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import VideoPlayer from '@/components/VideoPlayer';

const Checklist = () => {
  const { currentTenant } = useTenantState();
  const { isSuperAdmin } = useAuthState();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'detail' | 'checklist' | 'history' | 'notes'>(
    isSuperAdmin ? 'detail' : 'checklist',
  );
  const [creationFailed, setCreationFailed] = useState(false);
  const hasTriedCreation = useRef(false);
  const [confirmationNote, setConfirmationNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const videoLink = companyProfileSteps.find(step => step.step_name.toLowerCase() === 'checklist')?.video_link;

  // Handle URL hash to switch tabs
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash === 'checklist') {
      setActiveTab('checklist');
    } else if (hash === 'detail') {
      setActiveTab('detail');
    } else if (hash === 'notes') {
      setActiveTab('notes');
    } else if (hash === 'history') {
      setActiveTab('history');
    }
  }, [location.hash]);

  const { data: companyData, isLoading: isLoadingCompanyData } = useGetCompanyData(currentTenant?.id);
  const companyDataId = companyData?.id;

  const {
    data: checklistInstance,
    isLoading: isLoadingInstance,
    isError: isInstanceError,
  } = useGetChecklistInstanceByCompany(currentTenant?.id, companyDataId);

  const shouldFetchTemplate = companyDataId && !isLoadingInstance && !checklistInstance;
  const { data: activeTemplate, isLoading: isLoadingTemplate } = useGetActiveChecklistTemplate(
    shouldFetchTemplate ? currentTenant?.id : undefined,
  );

  const createInstanceMutation = useCreateChecklistInstance();

  const updateResponseMutation = useUpdateChecklistResponse();
  const completeResponseMutation = useCompleteChecklistResponse();

  const confirmInstanceMutation = useConfirmChecklistInstance();

  const isReadOnly = useIsReadOnly();
  const startImplementationMutation = useStartImplementation();

  // Normalize the stored timestamp to a plain YYYY-MM-DD for the date input/card.
  const implementationStartedDate = checklistInstance?.implementation_started_at
    ? new Date(checklistInstance.implementation_started_at).toISOString().slice(0, 10)
    : null;

  const handleSetImplementationDate = (date: string | null) => {
    if (!currentTenant?.id || !checklistInstance?.id || !companyDataId) return;
    startImplementationMutation.mutate({
      tenantId: currentTenant.id,
      instanceId: checklistInstance.id,
      companyDataId,
      implementationStartedAt: date,
    });
  };

  useEffect(() => {
    if (checklistInstance?.confirmation) {
      setConfirmationNote(checklistInstance.confirmation);
    }
  }, [checklistInstance?.confirmation]);

  useEffect(() => {
    const shouldCreateInstance =
      companyDataId &&
      !checklistInstance &&
      !isLoadingInstance &&
      activeTemplate &&
      !isLoadingTemplate &&
      !hasTriedCreation.current &&
      !creationFailed;

    if (shouldCreateInstance) {
      hasTriedCreation.current = true;
      createInstanceMutation.mutate(
        {
          tenantId: currentTenant!.id,
          payload: {
            company_data_id: companyDataId,
            template_id: activeTemplate.id,
          },
        },
        {
          onSuccess: () => {
            setCreationFailed(false);
          },
          onError: () => {
            setCreationFailed(true);
            hasTriedCreation.current = false;
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyDataId,
    checklistInstance,
    isLoadingInstance,
    activeTemplate,
    isLoadingTemplate,
    currentTenant,
    creationFailed,
  ]);

  const handleUpdateResponse = (responseId: number, field: string, value: string) => {
    if (!currentTenant?.id) return;

    let payload: Record<string, unknown> = { [field]: value };
    if (field === 'document_urls') {
      try {
        payload = { document_urls: JSON.parse(value) };
      } catch {
        payload = { document_urls: [] };
      }
    } else if (field === 'status') {
      payload.is_completed = value === 'done';
    }

    updateResponseMutation.mutate({
      tenantId: currentTenant.id,
      responseId,
      payload,
    });
  };

  if (isLoadingCompanyData || isLoadingInstance) {
    return <ChecklistSkeleton />;
  }

  if (shouldFetchTemplate && isLoadingTemplate) {
    return <ChecklistSkeleton />;
  }

  if (!companyDataId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-20 h-20 text-amber-500 mx-auto mb-4" />
          <p className="text-xl font-semibold text-gray-900 mb-2">Data perusahaan tidak ditemukan</p>
          <p className="text-sm text-gray-600">
            Silakan lengkapi data perusahaan terlebih dahulu sebelum mengakses checklist
          </p>
        </div>
      </div>
    );
  }

  if (isInstanceError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
          <p className="text-xl font-semibold text-gray-900 mb-2">Terjadi kesalahan saat memuat checklist</p>
          <p className="text-sm text-gray-600">Silakan coba lagi nanti</p>
        </div>
      </div>
    );
  }

  if (!activeTemplate && !checklistInstance) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-20 h-20 text-amber-500 mx-auto mb-4" />
          <p className="text-xl font-semibold text-gray-900 mb-2">Tidak ada template checklist yang aktif</p>
          <p className="text-sm text-gray-600">Silakan hubungi administrator untuk mengaktifkan template checklist</p>
        </div>
      </div>
    );
  }

  if (createInstanceMutation.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-20 h-20 text-blue-500 mx-auto mb-4 animate-spin" />
          <p className="text-xl font-semibold text-gray-900 mb-2">Sedang membuat instance checklist...</p>
          <p className="text-sm text-gray-600">Silakan tunggu sebentar</p>
        </div>
      </div>
    );
  }

  if (creationFailed) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
          <p className="text-xl font-semibold text-gray-900 mb-2">Gagal membuat instance checklist</p>
          <p className="text-sm text-gray-600 mb-4">
            Terjadi kesalahan saat membuat checklist. Silakan coba lagi nanti.
          </p>
          <DefaultButton
            type="button"
            onClick={() => {
              setCreationFailed(false);
            }}
          >
            Retry
          </DefaultButton>
        </div>
      </div>
    );
  }

  const template = checklistInstance?.template;
  const sections = template?.sections || [];
  const responses = checklistInstance?.responses || [];
  const isChecklistTabActive = activeTab === 'checklist';
  const showChecklistProgress = Boolean(checklistInstance && isChecklistTabActive);
  const showChecklistFooter = Boolean(checklistInstance && isChecklistTabActive && (isSuperAdmin || confirmationNote));

  // Progress counters for the "Progres N / N — %" card.
  const completedCount = responses.filter(r => r.status === 'done' || r.is_completed).length;
  const totalCount = responses.length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <section className="w-full">
      <div className="pt-5 px-5 pb-8">
        {videoLink && (
          <div className="flex justify-end mb-4">
            <VideoPlayer url={videoLink} title="Checklist" />
          </div>
        )}

        {/* AOS Implementation Checklist — intro + progress + start-date controls */}
        {showChecklistProgress && (
          <div className="mb-4 space-y-4">
            <div>
              <h2 className="text-lg font-bold text-blue-600 dark:text-blue-400">AOS Implementation Checklist</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Selamat! Seluruh langkah perancangan sistem bisnis AOS telah selesai dirumuskan. Berikut adalah daftar
                checklist tindakan implementasi operasional untuk dijalankan di perusahaan Anda.
              </p>
            </div>

            {/* Progress bar — compact, inline */}
            <div className="inline-flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2.5 shadow-sm">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">
                Progres: {completedCount} / {totalCount}
              </span>
              <div className="w-32 h-2.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${progressPct}%`, background: '#FD6118' }}
                />
              </div>
              <span className="text-sm font-bold text-blue-600 dark:text-blue-400 whitespace-nowrap">
                {progressPct}%
              </span>
            </div>

            <ImplementationStartCard
              startedAt={implementationStartedDate}
              isReadOnly={isReadOnly}
              isSaving={startImplementationMutation.isLoading}
              onSubmit={handleSetImplementationDate}
            />
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex -mb-px overflow-x-auto no-scrollbar">
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={() => setActiveTab('detail')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex-shrink-0 ${
                    activeTab === 'detail'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  Detail
                </button>
              )}
              <button
                type="button"
                onClick={() => setActiveTab('checklist')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex-shrink-0 ${
                  activeTab === 'checklist'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                Checklist
              </button>
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={() => setActiveTab('notes')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex-shrink-0 ${
                    activeTab === 'notes'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  Catatan
                </button>
              )}
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={() => setActiveTab('history')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex-shrink-0 ${
                    activeTab === 'history'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  Riwayat
                </button>
              )}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {isSuperAdmin && activeTab === 'detail' && checklistInstance?.company_data && (
              <ChecklistDetail companyData={checklistInstance.company_data} />
            )}
            {activeTab === 'checklist' && (
              <>
                {sections.length === 0 && (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                    <p className="font-medium">Template checklist tidak memiliki kategori</p>
                  </div>
                )}

                {sections.length > 0 && currentTenant && (
                  <ChecklistView
                    sections={sections}
                    responses={responses}
                    companyData={companyData}
                    tenantId={currentTenant.id}
                    isSuperAdmin={!!isSuperAdmin}
                    implementationStartedAt={implementationStartedDate}
                    onUpdateResponse={handleUpdateResponse}
                  />
                )}
              </>
            )}

            {isSuperAdmin && activeTab === 'notes' && checklistInstance && currentTenant && (
              <ChecklistNotes tenantId={currentTenant.id} instanceId={checklistInstance.id} />
            )}

            {isSuperAdmin && activeTab === 'history' && checklistInstance && currentTenant && (
              <ChecklistLog tenantId={currentTenant.id} instanceId={checklistInstance.id} />
            )}
          </div>

          {showChecklistFooter && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-6">
              {isSuperAdmin ? (
                <form
                  onSubmit={e => {
                    e.preventDefault();
                    if (!currentTenant?.id || !checklistInstance?.id) return;

                    setIsSubmitting(true);
                    confirmInstanceMutation.mutate(
                      {
                        tenantId: currentTenant.id,
                        instanceId: checklistInstance.id,
                        companyDataId: checklistInstance.company_data_id,
                        payload: {
                          confirmation: confirmationNote,
                        },
                      },
                      {
                        onSettled: () => {
                          setIsSubmitting(false);
                        },
                      },
                    );
                  }}
                >
                  <label
                    htmlFor="konfirmasi"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Catatan Penilaian
                  </label>
                  <textarea
                    id="konfirmasi"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Tambahkan catatan penilaian..."
                    rows={3}
                    value={confirmationNote}
                    onChange={e => setConfirmationNote(e.target.value)}
                  />
                  <section className="mt-3">
                    <SubmitButton
                      type="submit"
                      disabled={isSubmitting || !confirmationNote.trim()}
                      className="flex items-center gap-1"
                    >
                      <Check size={16} />
                      {isSubmitting ? 'Menyimpan...' : 'Simpan'}
                    </SubmitButton>
                  </section>
                </form>
              ) : (
                confirmationNote && (
                  <div>
                    <label
                      htmlFor="konfirmasi-readonly"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Catatan Penilaian
                    </label>
                    <textarea
                      id="konfirmasi-readonly"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 cursor-not-allowed"
                      rows={3}
                      value={confirmationNote}
                      readOnly
                      disabled
                    />
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Checklist;
